import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CornerDownRight, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";

import { getBounceCause, getEmailStatus, type LastSentEmail, type Tone } from "./email-status";

/** The outcome is a word inside a sentence here, not a badge, so the tone has to come as ink. */
const TONE_CLASS: Record<Tone, string> = {
  success: "text-emerald-700",
  warning: "text-amber-700",
  error: "text-red-700",
  secondary: "text-gray-600",
};

/**
 * What became of the last message we sent this family, hanging off the address it went to.
 *
 * Renders nothing when there is none. That is the normal state for an acquisition whose sends all
 * predate the `family_claim_acquisition_id` link, so it has to read as "nothing to say here"
 * rather than as an empty slot.
 */
export function LastSentEmailLine({ email }: { email: LastSentEmail | null | undefined }) {
  if (!email) {
    return null;
  }

  // A bounce cause supersedes the status rather than joining it: "Rejeté · Adresse inexistante"
  // says one thing twice, and the cause is the half a jurist can act on. It also covers the case
  // the status cannot — an unsubscribe leaves the message DELIVERED, because it was.
  const outcome = email.bounceCause ? getBounceCause(email.bounceCause) : getEmailStatus(email.status);

  return (
    <div className="mt-1 flex items-start gap-1.5 pl-7 text-xs text-gray-500">
      <CornerDownRight aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
      {/* `min-w-0` is what lets the subject below actually truncate inside the flex row. */}
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-x-1.5">
          <span>
            Dernier email envoyé le{" "}
            {format(new Date(email.messageDate), "d MMM yyyy 'à' HH:mm", { locale: fr })} -
          </span>
          <span className={cn("inline-flex items-center gap-1 font-medium", TONE_CLASS[outcome.tone])}>
            {email.bounceCause ? (
              <TriangleAlert aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            ) : null}
            {outcome.label}
          </span>
        </p>
        {/* Subjects run long — the line keeps its shape and the full one is a hover away. */}
        <p className="truncate italic" title={email.subject}>
          « {email.subject} »
        </p>
      </div>
    </div>
  );
}
